import { Avatar, Drawer, Image, Layout, Menu, Tooltip } from 'antd'
import React from 'react'
import * as fcIcon from 'react-icons/all'
import { useDispatch, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import { Link, useHistory, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import avatarImg from '../assets/images/avatar.png'
import logoImage from '../assets/images/logo192.png'
import { languages, translate } from '../languages'
import { ApiMethodEnum, UserTypeEnum } from '../models'
import { RootState } from '../stores'
import { logout, setLanguage } from '../stores/actions'

const menuItemIconFontSize = '1rem'
const menuItemIconMarginBottom = '-2.25px'

const Logo = styled.div`
  padding: 0 15px;
  float: left;
  width: fit-content;
  color: white;
  font-weight: 600;
`

const ToggleNavbar = styled(fcIcon.GiHamburgerMenu)`
  font-size: 1.5rem;
  color: white;
  cursor: pointer;
  transition: color 0.3s;
  float: right;
  margin: 20px 15px 0 0;
`

const StyledMenu = styled(Menu)`
  background: linear-gradient(87deg, rgb(0, 184, 216), rgb(17, 113, 229));
`

const MItem = styled(Menu.Item)`
  &.ant-menu-item {
    color: #000;
    @media screen and (min-width: 1300px) {
      color: #fff;
    }
    a {
      color: inherit;
      :hover {
        color: inherit;
      }
    }
  }

  &.ant-menu-item-selected,
  &.ant-menu-item:hover,
  &.ant-menu-item:active {
    color: #0364d3 !important;
    @media screen and (min-width: 1300px) {
      color: #f8fc13 !important;
    }
  }

  .ant-menu-item-icon {
    font-size: 1rem;
    margin-bottom: -2.25px;
  }
`

const MSubItem = styled(Menu.SubMenu)`
  &.ant-menu-submenu {
    color: #fff;
  }

  &.ant-menu-submenu-selected,
  &.ant-menu-submenu-open,
  &.ant-menu-submenu-active {
    color: #e7ff14 !important;
  }

  .ant-menu-submenu-title:hover {
    color: inherit;
  }
`

type MenuItem = {
  name: string
  path: string
  icon: React.ReactNode
  onClick?: () => void
  children?: MenuItem[]
}

export const HeaderComponent: React.FC = () => {
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()
  const { language } = useSelector((state: RootState) => state.language)
  const { profile } = useSelector((state: RootState) => state.auth)
  const { apis } = useSelector((state: RootState) => state.api)
  const [showDrawer, setShowDrawer] = React.useState(false)
  const isDesktopScreen = useMediaQuery({ minWidth: '1300px' })

  React.useEffect(() => {
    if (isDesktopScreen) {
      setShowDrawer(false)
    }
    // eslint-disable-next-line
  }, [isDesktopScreen])

  const commonMenuItem: MenuItem[] = React.useMemo(
    () => [
      {
        name: translate('common.dashboard', language),
        path: '/',
        icon: <fcIcon.FcSelfServiceKiosk size={15} />,
      },
      {
        name: translate('common.api', language),
        path: '/apis',
        icon: <fcIcon.FcParallelTasks />,
      },
      {
        name: translate('common.station', language),
        path: '/stations',
        icon: <fcIcon.FcShop style={{ margin: '0 10px -1px 0' }} />,
        children: [
          {
            name: `${translate('common.manage', language)} ${translate(
              'api.name.stations',
              language
            )}`,
            path: '/stations',
            icon: <fcIcon.FcShop />,
          },
          {
            name: `${translate('common.manage', language)} ${translate(
              'api.name.tanks',
              language
            )}`,
            path: '/tanks',
            icon: <fcIcon.FcDatabase />,
          },
          {
            name: `${translate('common.manage', language)} ${translate(
              'api.name.pumps',
              language
            )}`,
            path: '/pumps',
            icon: <fcIcon.GiGasPump />,
          },
        ],
      },
      {
        name: translate('common.transaction', language),
        path: '/transactions',
        icon: (
          <fcIcon.AiOutlineTransaction style={{ margin: '0 10px -1px 0' }} />
        ),
        children: [
          {
            name: `${translate('common.manage', language)} ${translate(
              'api.name.transactions',
              language
            )}`,
            path: '/transactions',
            icon: <fcIcon.AiOutlineTransaction />,
          },
          {
            name: `${translate('common.manage', language)} ${translate(
              'api.name.pump-shifts',
              language
            )}`,
            path: '/pump-shifts',
            icon: <fcIcon.FaHandsHelping />,
          },
        ],
      },
      {
        name: translate('common.fuel', language),
        path: '/fuels',
        icon: <fcIcon.GiWaterDrop style={{ margin: '0 10px -1px 0' }} />,
        children: [
          {
            name: `${translate('common.manage', language)} ${translate(
              'api.name.fuels',
              language
            )}`,
            path: '/fuels',
            icon: <fcIcon.GiWaterDrop />,
          },
          {
            name: `${translate('common.manage', language)} ${translate(
              'api.name.suppliers',
              language
            )}`,
            path: '/suppliers',
            icon: <fcIcon.FcShipped />,
          },
          {
            name: `${translate('common.manage', language)} ${translate(
              'api.name.fuel-imports',
              language
            )}`,
            path: '/fuel-imports',
            icon: <fcIcon.FcFeedIn />,
          },
        ],
      },
      {
        name: translate('common.user', language),
        path: '/cards',
        icon: <fcIcon.FcConferenceCall style={{ margin: '0 10px -1px 0' }} />,
        children: [
          {
            name: `${translate('common.manage', language)} ${translate(
              'api.name.users',
              language
            )}`,
            path: '/users',
            icon: <fcIcon.FcConferenceCall />,
          },
          {
            name: `${translate('common.manage', language)} ${translate(
              'api.name.cards',
              language
            )}`,
            path: '/cards',
            icon: <fcIcon.FcSimCardChip />,
          },
        ],
      },
      {
        name: translate('common.work', language),
        path: '/employees',
        icon: (
          <fcIcon.MdTransferWithinAStation
            style={{ margin: '0 10px -1px 0' }}
          />
        ),
        children: [
          {
            name: `${translate('common.manage', language)} ${translate(
              'api.name.employees',
              language
            )}`,
            path: '/employees',
            icon: <fcIcon.MdTransferWithinAStation />,
          },
          {
            name: `${translate('common.manage', language)} ${translate(
              'api.name.shifts',
              language
            )}`,
            path: '/shifts',
            icon: <fcIcon.FcAlarmClock />,
          },
          {
            name: `${translate('common.manage', language)} ${translate(
              'api.name.work-schedules',
              language
            )}`,
            path: '/work-schedules',
            icon: <fcIcon.FcCalendar />,
          },
        ],
      },
      {
        name: translate('common.finance', language),
        path: '/debts',
        icon: <fcIcon.FcMoneyTransfer style={{ margin: '0 10px -1px 0' }} />,
        children: [
          {
            name: `${translate('common.manage', language)} ${translate(
              'api.name.receipts',
              language
            )}`,
            path: '/receipts',
            icon: <fcIcon.GiReceiveMoney color="#02da4a" />,
          },
          {
            name: `${translate('common.manage', language)} ${translate(
              'api.name.expenses',
              language
            )}`,
            path: '/expenses',
            icon: <fcIcon.GiPayMoney color="#fa4417" />,
          },
          {
            name: `${translate('common.manage', language)} ${translate(
              'api.name.debts',
              language
            )}`,
            path: '/debts',
            icon: <fcIcon.FcDebt />,
          },
        ],
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [language]
  )

  const accessibleMenuItems = React.useMemo(() => {
    if (profile?.userType.type === UserTypeEnum.ADMIN) {
      return commonMenuItem
    }
    return commonMenuItem
      .filter((commonMItem) =>
        commonMItem.path === '/'
          ? apis.filter((item) =>
              ['/fuel-statistic', '/tank-statistic'].includes(item.path)
            ).length
          : commonMItem.children
          ? commonMItem.children.filter(
              (item) =>
                apis.filter(
                  (api) =>
                    api.method === ApiMethodEnum.GET && api.path === item.path
                ).length
            ).length
          : apis.filter(
              (item) =>
                item.method === ApiMethodEnum.GET &&
                item.path === commonMItem.path
            ).length
      )
      .map((item) => {
        return {
          ...item,
          children: item.children?.filter((child) =>
            apis
              .filter((api) => api.method === ApiMethodEnum.GET)
              .map((api) => api.path)
              .includes(child.path)
          ),
        }
      })
  }, [apis, commonMenuItem, profile?.userType])

  const languageMenuItem: MenuItem = {
    name: translate('common.language', language),
    icon: (
      <fcIcon.FcFaq
        style={{
          fontSize: menuItemIconFontSize,
          marginBottom: menuItemIconMarginBottom,
          marginRight: '10px',
        }}
      />
    ),
    path: '/language',
    children: languages.map((item) => {
      return {
        name: item.value,
        path: `/${item.prefix}`,
        icon: item.prefix,
        onClick: () => dispatch(setLanguage(item.prefix)),
      }
    }),
  }

  const actionMenuItem: MenuItem[] = [
    {
      name: translate('common.profile', language),
      path: '/profile',
      icon: (
        <fcIcon.FcManager
          style={{
            fontSize: menuItemIconFontSize,
            marginBottom: menuItemIconMarginBottom,
          }}
        />
      ),
      onClick: () => history.push('/profile'),
    },
    languageMenuItem,
    {
      name: translate('common.logout', language),
      path: '/login',
      icon: (
        <fcIcon.AiOutlineLogout
          style={{
            color: 'red',
            fontSize: menuItemIconFontSize,
            marginBottom: menuItemIconMarginBottom,
          }}
        />
      ),
      onClick: () => dispatch(logout()),
    },
  ]

  return (
    <Layout.Header
      style={{
        position: 'fixed',
        zIndex: 1,
        width: '100%',
        padding: '0',
        background:
          'linear-gradient(87deg, rgb(0, 184, 216), rgb(17, 113, 229))',
      }}
    >
      {isDesktopScreen ? (
        <>
          <StyledMenu
            mode="horizontal"
            selectedKeys={[
              ...accessibleMenuItems
                .map((item) => item.children || [item])
                .reduce((acc, curr) => [...curr, ...acc], [])
                .map((item) => item.path),
              '/profile',
            ].filter((item) => item === location.pathname)}
          >
            <Avatar
              style={{ margin: '-5px 0 0 10px' }}
              src={logoImage}
              shape="square"
              size={{ xs: 40, sm: 40, md: 45, lg: 50, xl: 50, xxl: 50 }}
            />
            {profile &&
              accessibleMenuItems.map((item) =>
                item.children ? (
                  <MSubItem icon={item.icon} title={item.name} key={item.path}>
                    {item.children.map((child) => (
                      <Menu.Item key={child.path} onClick={child.onClick}>
                        <Link to={child.path}>{child.name}</Link>
                      </Menu.Item>
                    ))}
                  </MSubItem>
                ) : (
                  <MItem
                    key={item.path}
                    icon={item.icon}
                    onClick={item.onClick}
                  >
                    <Link to={item.path}>{item.name}</Link>
                  </MItem>
                )
              )}
            <Menu.SubMenu
              style={{ color: 'white', float: 'right' }}
              icon={
                <fcIcon.AiFillSetting
                  style={{
                    fontSize: menuItemIconFontSize,
                    marginBottom: '-3px',
                  }}
                />
              }
              title={
                <span style={{ marginLeft: '5px' }}>
                  {translate('common.setting', language)}
                </span>
              }
              key={languageMenuItem.path}
            >
              <Menu.SubMenu
                key={languageMenuItem.name}
                icon={languageMenuItem.icon}
                title={languageMenuItem.name}
                style={{ color: 'black' }}
              >
                {languageMenuItem.children &&
                  languageMenuItem.children.map((child) => (
                    <Menu.Item key={child.path} onClick={child.onClick}>
                      <span
                        style={{
                          marginLeft: '20px',
                          fontSize: '0.7rem',
                          color:
                            child.path === `/${language}` ? 'blue' : 'black',
                        }}
                      >
                        {child.name}
                      </span>
                    </Menu.Item>
                  ))}
              </Menu.SubMenu>
              <Menu.Item
                key={'/logout'}
                icon={
                  <fcIcon.AiOutlineLogout
                    style={{ color: 'red', marginBottom: '-2.25px' }}
                  />
                }
                onClick={() => dispatch(logout())}
              >
                {translate('common.logout', language)}
              </Menu.Item>
            </Menu.SubMenu>
            {profile && (
              <Menu.Item style={{ float: 'right' }}>
                <Avatar
                  alt={profile.name}
                  style={{ marginTop: '-5px', backgroundColor: 'white' }}
                  src={<Image height="100%" src={avatarImg} />}
                />
                <Tooltip
                  placement="bottomRight"
                  title={translate('common.profile.view', language)}
                >
                  <span
                    style={{
                      marginLeft: '10px',
                      fontWeight: 'bold',
                      color: 'white',
                      fontFamily: 'Open Sans,sans-serif',
                    }}
                    onClick={() => history.push('/profile')}
                  >
                    {profile.name}
                  </span>
                </Tooltip>
              </Menu.Item>
            )}
          </StyledMenu>
        </>
      ) : (
        <>
          <Logo>
            <Avatar
              shape="square"
              src={logoImage}
              size={{ xs: 40, sm: 40, md: 45, lg: 50 }}
            />
          </Logo>
          <ToggleNavbar onClick={() => setShowDrawer(true)} />{' '}
          <Drawer
            title={
              <span style={{ fontSize: '0.6rem' }}>
                {`${translate('common.welcome', language)} ${profile?.name}`}
              </span>
            }
            placement="right"
            closable={true}
            closeIcon={<fcIcon.AiOutlineCloseCircle style={{ color: 'red' }} />}
            onClose={() => setShowDrawer(false)}
            visible={showDrawer}
          >
            <Menu
              selectedKeys={[
                ...accessibleMenuItems
                  .map((item) => item.children || [item])
                  .reduce((acc, curr) => [...curr, ...acc], [])
                  .map((item) => item.path),
                '/profile',
              ].filter((item) => item === location.pathname)}
              mode="inline"
              style={{ fontSize: '0.7rem', width: 'fit-content' }}
            >
              {profile &&
                accessibleMenuItems
                  .map((item) => item.children || [item])
                  .reduce((acc, curr) => [...curr, ...acc], [])
                  .map((item) => (
                    <MItem key={item.path} icon={item.icon}>
                      <Link to={item.path}>{item.name}</Link>
                    </MItem>
                  ))}
              <Menu.Divider />
              {actionMenuItem.map((item) =>
                item.children ? (
                  <Menu.SubMenu
                    key={item.name}
                    icon={item.icon}
                    title={item.name}
                    style={{ color: 'black' }}
                  >
                    {item.children.map((child) => (
                      <Menu.Item key={child.path} onClick={child.onClick}>
                        <span
                          style={{
                            marginLeft: '20px',
                            fontSize: '0.6rem',
                            color:
                              child.path === `/${language}` ? 'blue' : 'black',
                          }}
                        >
                          {child.name}
                        </span>
                      </Menu.Item>
                    ))}
                  </Menu.SubMenu>
                ) : (
                  profile && (
                    <MItem
                      key={item.path}
                      icon={item.icon}
                      onClick={item.onClick}
                    >
                      <Link to={item.path}>{item.name}</Link>
                    </MItem>
                  )
                )
              )}
            </Menu>
          </Drawer>
        </>
      )}
    </Layout.Header>
  )
}
